import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrevious } from 'react-use';
import styled from 'styled-components';

import type { ColorFormat } from '#src/lib/colors.ts';
import { colorToNormalizedRgbArray } from '#src/lib/colors.ts';

import {
  drawVideo,
  hasDecodableFrame,
  isVideoPlaying,
  setupGLContext,
} from './helpers.ts';

const StyledCanvas = styled.canvas`
  display: block;
  width: inherit;
  object-fit: inherit;
  aspect-ratio: inherit;
  height: inherit;
`;

/**
 * Inspired from https://jakearchibald.com/2024/video-with-transparency/
 * To generate the AV1 file in FFMPEG: INPUT="in.mov" OUTPUT="av1.mp4" CRF=45 CPU=3 bash -c 'ffmpeg -y -i "$INPUT" -filter_complex "[0:v]format=pix_fmts=yuva444p[main]; [main]split[main][alpha]; [alpha]alphaextract[alpha]; [main][alpha]vstack" -pix_fmt yuv420p -an -c:v libaom-av1 -cpu-used "$CPU" -crf "$CRF" -pass 1 -f null /dev/null && ffmpeg -y -i "$INPUT" -filter_complex "[0:v]format=pix_fmts=yuva444p[main]; [main]split[main][alpha]; [alpha]alphaextract[alpha]; [main][alpha]vstack" -pix_fmt yuv420p -an -c:v libaom-av1 -cpu-used "$CPU" -crf "$CRF" -pass 2 -movflags +faststart "$OUTPUT"'
 * To generate the HEVC file in FFMPEG: INPUT="in.mov" OUTPUT="hevc.mp4" CRF=30 PRESET="veryslow" bash -c 'ffmpeg -y -i "$INPUT" -filter_complex "[0:v]format=pix_fmts=yuva444p[main]; [main]split[main][alpha]; [alpha]alphaextract[alpha]; [main][alpha]vstack" -pix_fmt yuv420p -an -c:v libx265 -preset "$PRESET" -crf "$CRF" -tag:v hvc1 -movflags +faststart "$OUTPUT"'
 */
type Props = {
  play: boolean;
  overlayColor?: ColorFormat;
  video: HTMLVideoElement;
};
export const TransparentVideoCanvas = ({
  play,
  overlayColor,
  video,
}: Props) => {
  const [context, setContext] = useState<WebGLRenderingContext | null>(null);
  const frameHandleRef = useRef<number>(0);
  const prevVideo = usePrevious(video);

  const refCallback = useCallback(
    (node: HTMLCanvasElement | null) => {
      if (node) {
        try {
          setContext(
            setupGLContext(
              node,
              overlayColor ? colorToNormalizedRgbArray(overlayColor) : undefined
            )
          );
        } catch {
          //Fail silently. The user will not see the video.
        }
      }
    },
    [overlayColor]
  );

  const playFrameRef = useRef<() => void>(() => {});

  useEffect(() => {
    const onVideoEnded = () => {
      cancelAnimationFrame(frameHandleRef.current);
    };

    if (play && !!context && !isVideoPlaying(video)) {
      playFrameRef.current = () => {
        if (!context) return;
        if (hasDecodableFrame(video)) {
          drawVideo(context, video);
        }
        frameHandleRef.current = requestAnimationFrame(playFrameRef.current);
      };
      // Using setTimeout because video.play() only works in Safari if a task is queued.
      setTimeout(() => {
        video.play();
        video.addEventListener('ended', onVideoEnded);
        frameHandleRef.current = requestAnimationFrame(playFrameRef.current);
      }, 0);
    }
    const shouldReset = video !== prevVideo || (!play && isVideoPlaying(video));

    if (shouldReset) {
      cancelAnimationFrame(frameHandleRef.current);
      context?.clearColor(0, 0, 0, 0);
      context?.clear(context.COLOR_BUFFER_BIT);
      video.pause();
      // eslint-disable-next-line
      video.currentTime = 0;
    }

    return () => {
      video.removeEventListener('ended', onVideoEnded);
    };
  }, [play, video, prevVideo, context]);

  useEffect(() => {
    return () => {
      context?.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [context]);

  return <StyledCanvas ref={refCallback} />;
};
